import { useEffect, useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';

export type Lang = 'zh' | 'en';

interface LandingPageProps {
  onLogin: () => void;
  onWechatLogin?: () => void;
  lang: Lang;
  setLang: (lang: Lang) => void;
  onPublicPage: (page: 'terms' | 'privacy' | 'changelog') => void;
}

const t = {
  zh: {
    nav: {
      features: '功能',
      howItWorks: '流程',
      pricing: '定价',
      faq: '常见问题',
      contact: '联系我们',
      login: '登录',
      start: '免费开始',
    },
    hero: {
      tag: '企业自主研发的财务数据管理平台',
      title: '把专业交易工具装进口袋',
      slogan: '数据驱动，决策不慌',
      subtitle:
        '听阅 TingYue 是东莞市沐辰日用品有限公司自主研发的财务数据分析与资产管理平台。实时行情、做T分析、组合管理、AI 多模型诊断与价格预警，全部集成在一个安全、私有的环境中，服务于公司自有资金的投资研究与内部管理。',
      ctaPrimary: '立即开始',
      ctaSecondary: '查看功能',
      stat1Label: '支持市场',
      stat1Value: 'A股 / 港股 / 美股',
      stat2Label: '数据延迟',
      stat2Value: '秒级',
      stat3Label: '部署方式',
      stat3Value: '私有 / 自托管',
    },
    markets: {
      title: '覆盖主要市场',
      subtitle: '沪深港美核心资产，一个账户统一跟踪',
    },
    preview: {
      title: '产品界面预览',
      subtitle: '清爽的界面设计，复杂数据也能一目了然',
      watchlist: { title: '自选股行情', desc: '实时价格、涨跌幅、市值一目了然' },
      ai: { title: 'AI 分析对话', desc: '多模型综合结论，支持追问与记忆' },
      portfolio: { title: '组合仪表盘', desc: '持仓、收益、风险指标一屏掌握' },
    },
    features: {
      title: '为交易者打造的工具箱',
      subtitle: '从实时行情到 AI 复盘，每个环节都为你提效',
      items: [
        {
          title: '实时行情与做T助手',
          desc: '接入 WebSocket 实时行情，自动计算加权成本、FIFO 配对盈亏与现金流，辅助日内做T决策。',
        },
        {
          title: '组合管理',
          desc: '多组合持仓跟踪，基于真实历史 K 线计算每日 NAV，支持收益归因、风险指标与最大回撤分析。',
        },
        {
          title: 'AI 多模型分析',
          desc: '支持你自己的大模型 API Key，多模型投票给出综合结论，保留会话记忆，支持多轮深度问答。',
        },
        {
          title: '智能选股器',
          desc: '按价格、涨跌幅、振幅、成交量、市值、PE、PB、ROE、营收增速、股息率等多维条件快速筛选标的。',
        },
        {
          title: '统计与复盘',
          desc: '日/月/年盈亏日历、胜率、夏普比率、交易胜率，用数据驱动下一次决策。',
        },
        {
          title: '通知与价格预警',
          desc: '交易、AI 事件实时推送，目标价突破自动提醒，让你不再错过关键点位。',
        },
      ],
    },
    howItWorks: {
      title: '三步开始量化交易',
      steps: [
        { title: '添加自选股', desc: '导入你关注的沪深港美标的，系统开始实时跟踪行情。' },
        { title: '记录交易', desc: '买入、卖出、分红、送股一键录入，自动计算成本与已实现盈亏。' },
        { title: '分析与复盘', desc: '用 AI 诊断持仓、设置价格预警、导出统计报表，持续优化策略。' },
      ],
    },
    dashboard: {
      title: '一目了然的资产看板',
      subtitle: '组合净值、持仓结构、风险指标，所有关键信息集中呈现',
    },
    pricing: {
      title: '简单透明的定价',
      subtitle: '自托管版本免费使用，数据与密钥完全由你掌控',
      free: {
        name: '自托管版',
        price: '免费',
        period: '永久',
        features: [
          '全部核心功能',
          '无限自选股与组合',
          '私有 SQLite 数据存储',
          '自定义大模型 API Key',
          '社区支持',
        ],
        cta: '立即部署',
      },
      note: '未来可能推出云端托管与企业版，当前所有功能对个人用户完全免费。',
    },
    faq: {
      title: '常见问题',
      items: [
        { q: '我的数据存放在哪里？', a: '默认使用本地 SQLite 数据库存储在你的服务器或电脑上，我们不会收集你的交易数据或 API Key。' },
        { q: '是否需要提供大模型 API Key？', a: '是的。TingYue 不内置通用大模型，你需要在设置里添加自己的 OpenAI、DeepSeek、Claude 等 API Key，系统会加密存储。' },
        { q: '支持哪些市场？', a: '目前核心支持 A 股，港股与美股的行情与交易记录也已纳入规划，可通过配置文件逐步扩展。' },
        { q: '微信扫码登录需要准备什么？', a: '需要在微信开放平台注册网站应用，拥有备案域名与企业资质，然后在 .env 中配置 AppID 与 AppSecret。' },
      ],
    },
    cta: {
      title: '准备好掌控你的投资了吗？',
      subtitle: '部署属于你自己的量化助手，从今天开始做数据驱动的决策。',
      button: '免费开始使用',
    },
    footer: {
      tagline: '企业财务数据分析与资产管理系统',
      links: {
        product: '产品',
        company: '关于',
        legal: '法律',
      },
      product: ['功能', '定价', '常见问题', '更新日志'],
      company: ['关于我们', '联系我们'],
      legal: ['服务条款', '隐私政策'],
      companyName: '东莞市沐辰日用品有限公司',
      ownership: '听阅 TingYue 为本公司自主研发的财务数据分析与资产管理系统，服务于公司自有资金的投资研究与内部管理',
      rights: '版权所有',
      icp: '粤ICP备2023146695号-3',
    },
  },
  en: {
    nav: {
      features: 'Features',
      howItWorks: 'How It Works',
      pricing: 'Pricing',
      faq: 'FAQ',
      contact: 'Contact',
      login: 'Log in',
      start: 'Get Started',
    },
    hero: {
      tag: 'An in-house financial data management platform',
      title: 'Put professional trading tools in your pocket',
      slogan: 'Data-driven decisions, no more guessing',
      subtitle:
        'TingYue is a financial data analysis and asset management platform developed in-house by Dongguan Muchen Daily Products Co., Ltd. Real-time quotes, T-trade analytics, portfolio management, multi-model AI diagnostics and price alerts — all in a secure, private environment serving the company\u2019s proprietary investment research and internal management.',
      ctaPrimary: 'Get Started',
      ctaSecondary: 'Explore Features',
      stat1Label: 'Markets',
      stat1Value: 'A-Share / HK / US',
      stat2Label: 'Data Latency',
      stat2Value: 'Sub-second',
      stat3Label: 'Deployment',
      stat3Value: 'Private / Self-hosted',
    },
    markets: {
      title: 'Coverage Across Major Markets',
      subtitle: 'Track core assets across China, Hong Kong and US markets in one account',
    },
    preview: {
      title: 'Product Preview',
      subtitle: 'Clean interface design that makes complex data easy to read',
      watchlist: { title: 'Watchlist Quotes', desc: 'Real-time prices, change % and market cap at a glance' },
      ai: { title: 'AI Analysis Chat', desc: 'Multi-model consensus with follow-up questions and memory' },
      portfolio: { title: 'Portfolio Dashboard', desc: 'Holdings, returns and risk metrics in one view' },
    },
    features: {
      title: 'A toolbox built for traders',
      subtitle: 'From real-time quotes to AI-driven review, every step is designed to make you more efficient',
      items: [
        {
          title: 'Real-time Quotes & T-Trade Helper',
          desc: 'WebSocket-powered real-time quotes with weighted cost, FIFO pairing P&L and cash-flow calculations to assist intraday T-trade decisions.',
        },
        {
          title: 'Portfolio Management',
          desc: 'Track multiple portfolios, compute real daily NAV from historical K-lines, and analyze returns attribution, risk metrics and max drawdown.',
        },
        {
          title: 'Multi-Model AI Analysis',
          desc: 'Bring your own LLM API keys. Multiple models vote on a consensus conclusion, with conversation memory and multi-turn deep Q&A.',
        },
        {
          title: 'Smart Screener',
          desc: 'Screen stocks by price, change %, amplitude, turnover, market cap, PE, PB, ROE, revenue growth, dividend yield and more.',
        },
        {
          title: 'Statistics & Review',
          desc: 'Daily/monthly/yearly P&L calendar, win rate, Sharpe ratio and trade analytics to drive your next decision with data.',
        },
        {
          title: 'Alerts & Notifications',
          desc: 'Real-time push notifications for trades and AI events, plus target-price alerts so you never miss a key level.',
        },
      ],
    },
    howItWorks: {
      title: 'Start quant trading in three steps',
      steps: [
        { title: 'Add Watchlist', desc: 'Import the A-share, HK and US tickers you follow and start tracking real-time quotes.' },
        { title: 'Record Trades', desc: 'Log buys, sells, dividends and splits in one click; costs and realized P&L are calculated automatically.' },
        { title: 'Analyze & Review', desc: 'Diagnose positions with AI, set price alerts, and export reports to continuously refine your strategy.' },
      ],
    },
    dashboard: {
      title: 'A clear view of your assets',
      subtitle: 'Portfolio NAV, holdings structure and risk metrics — all key information in one place',
    },
    pricing: {
      title: 'Simple, transparent pricing',
      subtitle: 'The self-hosted version is free to use; your data and keys stay under your control',
      free: {
        name: 'Self-Hosted',
        price: 'Free',
        period: 'forever',
        features: [
          'All core features',
          'Unlimited watchlists and portfolios',
          'Private SQLite data storage',
          'Custom LLM API keys',
          'Community support',
        ],
        cta: 'Deploy Now',
      },
      note: 'A cloud-hosted or enterprise edition may be introduced later. All current features are free for personal use.',
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        { q: 'Where is my data stored?', a: 'By default, TingYue uses a local SQLite database on your own server or computer. We do not collect your trading data or API keys.' },
        { q: 'Do I need to provide an LLM API key?', a: 'Yes. TingYue does not bundle a general-purpose LLM. You add your own OpenAI, DeepSeek, Claude or compatible key in Settings, and it is encrypted at rest.' },
        { q: 'Which markets are supported?', a: 'A-shares are supported today. Hong Kong and US quotes and trade records are on the roadmap and can be enabled via configuration.' },
        { q: 'What do I need for WeChat QR login?', a: 'You need a WeChat Open Platform website app, an ICP-registered domain, and enterprise qualifications. Then configure AppID and AppSecret in your .env file.' },
      ],
    },
    cta: {
      title: 'Ready to take control of your investments?',
      subtitle: 'Deploy your own quantitative assistant and start making data-driven decisions today.',
      button: 'Start for Free',
    },
    footer: {
      tagline: 'In-house financial data analysis & asset management system',
      links: {
        product: 'Product',
        company: 'Company',
        legal: 'Legal',
      },
      product: ['Features', 'Pricing', 'FAQ', 'Changelog'],
      company: ['About', 'Contact'],
      legal: ['Terms of Service', 'Privacy Policy'],
      companyName: 'Dongguan Muchen Daily Products Co., Ltd.',
      ownership: 'TingYue is a financial data analysis and asset management system developed in-house by the company, serving its proprietary investment research and internal management',
      rights: 'All rights reserved',
      icp: '粤ICP备2023146695号-3',
    },
  },
};

function HeroChart() {
  const candles = [
    { x: 60, o: 220, c: 200, h: 230, l: 190, up: false },
    { x: 110, o: 205, c: 240, h: 250, l: 200, up: true },
    { x: 160, o: 238, c: 230, h: 245, l: 220, up: false },
    { x: 210, o: 232, c: 265, h: 270, l: 225, up: true },
    { x: 260, o: 262, c: 250, h: 268, l: 240, up: false },
    { x: 310, o: 252, c: 285, h: 295, l: 245, up: true },
    { x: 360, o: 282, c: 270, h: 290, l: 260, up: false },
    { x: 410, o: 272, c: 305, h: 315, l: 265, up: true },
    { x: 460, o: 302, c: 290, h: 310, l: 280, up: false },
    { x: 510, o: 292, c: 325, h: 335, l: 285, up: true },
    { x: 560, o: 322, c: 310, h: 330, l: 300, up: false },
    { x: 610, o: 312, c: 345, h: 355, l: 305, up: true },
    { x: 660, o: 342, c: 330, h: 350, l: 320, up: false },
    { x: 710, o: 332, c: 360, h: 370, l: 325, up: true },
  ];

  const linePoints = candles.map((d) => `${d.x},${d.c}`).join(' ');

  return (
    <div className="relative w-full max-w-2xl mx-auto rounded-2xl border border-border bg-card p-4 shadow-lg">
      <div className="flex items-center justify-between mb-4 px-2">
        <div>
          <div className="text-sm font-medium">000001.SZ</div>
          <div className="text-xs text-muted-foreground">平安银行</div>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold text-red-600">3,608.45</div>
          <div className="text-xs text-red-600">+1.24%</div>
        </div>
      </div>
      <svg viewBox="0 0 770 380" className="w-full h-auto">
        {[0, 1, 2, 3, 4].map((i) => (
          <line
            key={`h-${i}`}
            x1="0"
            y1={70 + i * 65}
            x2="770"
            y2={70 + i * 65}
            className="stroke-border"
            strokeWidth="1"
            strokeDasharray="4 4"
          />
        ))}
        <polyline
          fill="none"
          stroke="#533afd"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={linePoints}
        />
        {candles.map((d, i) => {
          const color = d.up ? '#ef4444' : '#22c55e';
          const top = Math.min(d.o, d.c);
          const height = Math.max(Math.abs(d.c - d.o), 2);
          return (
            <g key={i}>
              <line x1={d.x} y1={d.h} x2={d.x} y2={d.l} stroke={color} strokeWidth="2" />
              <rect x={d.x - 10} y={top} width="20" height={height} rx="2" fill={color} />
            </g>
          );
        })}
      </svg>
      <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground px-2">
        <span>MA5: 3,512.80</span>
        <span>MA20: 3,428.60</span>
        <span>Vol: 12.4B</span>
      </div>
    </div>
  );
}

function PreviewCard({ title, desc, children }: { title: string; desc: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground">{desc}</p>
        </div>
        <div className="flex gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-red-400" />
          <div className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
          <div className="h-2.5 w-2.5 rounded-full bg-green-400" />
        </div>
      </div>
      <div className="rounded-xl border border-border bg-background p-3">{children}</div>
    </div>
  );
}

function SectionTitle({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="text-center max-w-2xl mx-auto mb-12">
      <h2 className="text-3xl font-semibold tracking-[-0.02em] mb-4">{title}</h2>
      <p className="text-muted-foreground">{subtitle}</p>
    </div>
  );
}

function FeatureIcon({ index }: { index: number }) {
  const icons = [
    <path key="1" d="M3 3v18h18" />,
    <path key="2" d="M18 20V10M12 20V4M6 20v-6" />,
    <path key="3" d="M12 2a10 10 0 1 0 10 10H12V2z" />,
    <path key="4" d="M22 12h-4l-3 9L9 3l-3 9H2" />,
    <circle key="5" cx="12" cy="12" r="10" />,
    <path key="6" d="M6 8a6 6 0 0 1 12 0c0 7-6 11-6 11S6 15 6 8Z" />,
  ];
  return (
    <svg
      className="h-6 w-6"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {icons[index]}
    </svg>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border">
      <button
        className="w-full py-4 flex items-center justify-between text-left focus:outline-none"
        onClick={() => setOpen(!open)}
      >
        <span className="font-medium">{q}</span>
        <span
          className={`ml-4 transform transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-muted-foreground">
            <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>
      {open && <p className="pb-4 text-muted-foreground leading-relaxed">{a}</p>}
    </div>
  );
}

export function LandingPage({ onLogin, onWechatLogin, lang, setLang, onPublicPage }: LandingPageProps) {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const copy = t[lang];

  useEffect(() => {
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en';
  }, [lang]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  const navItems = [
    { label: copy.nav.features, id: 'features' },
    { label: copy.nav.howItWorks, id: 'how-it-works' },
    { label: copy.nav.pricing, id: 'pricing' },
    { label: copy.nav.faq, id: 'faq' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <header className="border-b border-border bg-background/95 backdrop-blur-[12px] sticky top-0 z-50">
        <div className="max-w-[1080px] mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/LOGO.png" alt="TingYue" className="h-8 w-auto" />
            <span className="text-lg font-semibold tracking-[-0.02em]">TingYue</span>
          </div>

          <nav className="hidden md:flex items-center gap-6 text-sm">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="text-muted-foreground hover:text-foreground transition"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setLang(lang === 'zh' ? 'en' : 'zh')}
              className="px-2 py-1 text-xs font-medium rounded-md border border-border hover:bg-secondary transition"
            >
              {lang === 'zh' ? 'EN' : '中'}
            </button>
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark')}
              className="p-2 rounded-md hover:bg-secondary text-muted-foreground transition hidden sm:block"
              title="切换主题"
            >
              {resolvedTheme === 'dark' ? (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" /></svg>
              ) : (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" /></svg>
              )}
            </button>
            <Button variant="ghost" size="sm" className="hidden sm:inline-flex" onClick={onLogin}>
              {copy.nav.login}
            </Button>
            <Button size="sm" className="bg-[#533afd] hover:bg-[#412fd9]" onClick={onLogin}>
              {copy.nav.start}
            </Button>
            <button
              className="md:hidden p-2 rounded-md hover:bg-secondary"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="menu"
            >
              {mobileOpen ? (
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
              ) : (
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
              )}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-border px-4 py-3 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="block w-full text-left py-2 text-sm text-muted-foreground hover:text-foreground"
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </header>

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[rgba(83,58,253,0.08)] via-transparent to-transparent" />
          <div className="max-w-[1080px] mx-auto px-4 md:px-6 py-16 md:py-24">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground mb-6">
                  {copy.hero.tag}
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-tight mb-4">
                  {copy.hero.title}
                </h1>
                <p className="text-lg font-medium text-[#533afd] mb-6">
                  {copy.hero.slogan}
                </p>
                <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                  {copy.hero.subtitle}
                </p>
                <div className="flex flex-col sm:flex-row flex-wrap gap-4 mb-10">
                  <Button size="lg" className="bg-[#533afd] hover:bg-[#412fd9]" onClick={onLogin}>
                    {copy.hero.ctaPrimary}
                  </Button>
                  <Button size="lg" variant="outline" onClick={() => scrollTo('features')}>
                    {copy.hero.ctaSecondary}
                  </Button>
                  {onWechatLogin && (
                    <Button size="lg" variant="outline" onClick={onWechatLogin} className="border-green-500 text-green-600 hover:bg-green-50 hover:text-green-700">
                      <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8.5 13a1 1 0 100-2 1 1 0 000 2zm5 0a1 1 0 100-2 1 1 0 000 2z" />
                        <path d="M8.8 1.6C4.2 2.6.7 6.3.7 10.7c0 2.6 1.4 5 3.6 6.6-.2.8-.7 2.2-1.4 3.2 2 .2 4.2-.6 5.4-1.4 1 .3 2.1.5 3.2.5 5.8 0 10.5-3.7 10.5-8.3 0-4.5-4.7-8.2-10.5-8.2-.9 0-1.8.1-2.7.5z" />
                      </svg>
                      微信扫码登录
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-6 border-t border-border pt-6">
                  <div>
                    <div className="text-sm font-medium">{copy.hero.stat1Label}</div>
                    <div className="text-xs text-muted-foreground">{copy.hero.stat1Value}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium">{copy.hero.stat2Label}</div>
                    <div className="text-xs text-muted-foreground">{copy.hero.stat2Value}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium">{copy.hero.stat3Label}</div>
                    <div className="text-xs text-muted-foreground">{copy.hero.stat3Value}</div>
                  </div>
                </div>
              </div>
              <HeroChart />
            </div>
          </div>
        </section>

        {/* Markets */}
        <section className="border-y border-border bg-card/50">
          <div className="max-w-[1080px] mx-auto px-4 md:px-6 py-10">
            <SectionTitle title={copy.markets.title} subtitle={copy.markets.subtitle} />
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8">
              {['Shanghai', 'Shenzhen', 'Hong Kong', 'Nasdaq', 'NYSE'].map((m) => (
                <div
                  key={m}
                  className="px-5 py-2.5 rounded-lg border border-border bg-background text-sm font-medium text-muted-foreground"
                >
                  {m}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Preview */}
        <section className="py-16 md:py-24 bg-card/50">
          <div className="max-w-[1080px] mx-auto px-4 md:px-6">
            <SectionTitle title={copy.preview.title} subtitle={copy.preview.subtitle} />
            <div className="grid md:grid-cols-3 gap-6">
              <PreviewCard title={copy.preview.watchlist.title} desc={copy.preview.watchlist.desc}>
                <div className="space-y-2">
                  {[
                    { name: '600519 贵州茅台', price: '1,688.00', change: '+1.20%' },
                    { name: '000001 平安银行', price: '11.35', change: '-0.87%' },
                    { name: '300750 宁德时代', price: '198.50', change: '+2.15%' },
                  ].map((row) => (
                    <div key={row.name} className="flex items-center justify-between text-xs py-1.5 border-b border-border last:border-0">
                      <span className="text-muted-foreground">{row.name}</span>
                      <div className="flex items-center gap-3">
                        <span>{row.price}</span>
                        <span className={row.change.startsWith('+') ? 'text-red-600' : 'text-green-700'}>{row.change}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </PreviewCard>
              <PreviewCard title={copy.preview.ai.title} desc={copy.preview.ai.desc}>
                <div className="space-y-3">
                  <div className="bg-secondary/50 rounded-lg p-2 text-xs text-muted-foreground">
                    {lang === 'zh' ? '如何看待当前持仓结构？' : 'What do you think of the current portfolio structure?'}
                  </div>
                  <div className="bg-[rgba(83,58,253,0.08)] rounded-lg p-2 text-xs text-foreground">
                    {lang === 'zh'
                      ? '当前仓位集中度偏高，建议关注单一标的风险，并设置止损预警。'
                      : 'Current concentration is high. Consider monitoring single-name risk and setting stop-loss alerts.'}
                  </div>
                </div>
              </PreviewCard>
              <PreviewCard title={copy.preview.portfolio.title} desc={copy.preview.portfolio.desc}>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{lang === 'zh' ? '总资产' : 'Total Assets'}</span>
                    <span className="font-medium">1,248,300.00</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{lang === 'zh' ? '累计收益' : 'Total Return'}</span>
                    <span className="font-medium text-red-600">+128,450.00</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{lang === 'zh' ? '最大回撤' : 'Max Drawdown'}</span>
                    <span className="font-medium">-8.12%</span>
                  </div>
                  <div className="h-12 flex items-end gap-1">
                    {[30, 45, 38, 55, 48, 62, 58, 70, 65, 78].map((h, i) => (
                      <div key={i} className="flex-1 rounded-t-sm bg-[#533afd]" style={{ height: `${h}%` }} />
                    ))}
                  </div>
                </div>
              </PreviewCard>
            </div>
          </div>
        </section>

        {/* Features */}
        <section id="features" className="py-16 md:py-24">
          <div className="max-w-[1080px] mx-auto px-4 md:px-6">
            <SectionTitle title={copy.features.title} subtitle={copy.features.subtitle} />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {copy.features.items.map((f, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-border bg-card p-6 shadow-sm hover:shadow-md transition"
                >
                  <div className="h-10 w-10 rounded-lg bg-[rgba(83,58,253,0.08)] flex items-center justify-center text-[#533afd] mb-4">
                    <FeatureIcon index={i} />
                  </div>
                  <h3 className="text-base font-semibold mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="py-16 md:py-24 bg-card/50">
          <div className="max-w-[1080px] mx-auto px-4 md:px-6">
            <SectionTitle title={copy.howItWorks.title} subtitle="" />
            <div className="grid md:grid-cols-3 gap-8">
              {copy.howItWorks.steps.map((s, i) => (
                <div key={i} className="relative">
                  <div className="text-5xl font-bold text-[rgba(83,58,253,0.72)] mb-4">0{i + 1}</div>
                  <h3 className="text-lg font-semibold mb-2">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                  {i < 2 && (
                    <div className="hidden md:block absolute top-8 left-[calc(100%-1rem)] w-[calc(100%-2rem)] h-px bg-border" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Dashboard preview */}
        <section id="dashboard" className="py-16 md:py-24">
          <div className="max-w-[1080px] mx-auto px-4 md:px-6">
            <SectionTitle title={copy.dashboard.title} subtitle={copy.dashboard.subtitle} />
            <div className="rounded-2xl border border-border bg-card p-6 md:p-10 shadow-sm">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="p-4 rounded-xl bg-secondary/50">
                  <div className="text-xs text-muted-foreground mb-1">{lang === 'zh' ? '总资产' : 'Total Assets'}</div>
                  <div className="text-2xl font-semibold">1,248,300.00</div>
                  <div className="text-xs text-red-600 mt-1">+2.34%</div>
                </div>
                <div className="p-4 rounded-xl bg-secondary/50">
                  <div className="text-xs text-muted-foreground mb-1">{lang === 'zh' ? '累计收益' : 'Total Return'}</div>
                  <div className="text-2xl font-semibold text-red-600">+128,450.00</div>
                  <div className="text-xs text-muted-foreground mt-1">{lang === 'zh' ? '自 2024-01' : 'Since 2024-01'}</div>
                </div>
                <div className="p-4 rounded-xl bg-secondary/50">
                  <div className="text-xs text-muted-foreground mb-1">{lang === 'zh' ? '最大回撤' : 'Max Drawdown'}</div>
                  <div className="text-2xl font-semibold">-8.12%</div>
                  <div className="text-xs text-muted-foreground mt-1">{lang === 'zh' ? '近一年' : 'Last 12M'}</div>
                </div>
              </div>
              <div className="h-48 md:h-64 rounded-xl border border-border bg-background flex items-end justify-between px-6 pb-6 pt-10 gap-2">
                {[35, 48, 42, 55, 50, 62, 58, 70, 65, 78, 72, 85].map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-t-sm bg-[rgba(83,58,253,0.15)] hover:bg-[#533afd] transition"
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="py-16 md:py-24 bg-card/50">
          <div className="max-w-[1080px] mx-auto px-4 md:px-6">
            <SectionTitle title={copy.pricing.title} subtitle={copy.pricing.subtitle} />
            <div className="max-w-md mx-auto rounded-2xl border border-border bg-card p-8 shadow-sm text-center">
              <div className="text-sm text-muted-foreground mb-2">{copy.pricing.free.name}</div>
              <div className="text-4xl font-bold mb-1">{copy.pricing.free.price}</div>
              <div className="text-xs text-muted-foreground mb-6">{copy.pricing.free.period}</div>
              <ul className="text-left space-y-3 mb-8">
                {copy.pricing.free.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <svg className="h-5 w-5 text-[#533afd] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    {f}
                  </li>
                ))}
              </ul>
              <Button className="w-full bg-[#533afd] hover:bg-[#412fd9]" onClick={onLogin}>
                {copy.pricing.free.cta}
              </Button>
            </div>
            <p className="text-center text-xs text-muted-foreground mt-6">{copy.pricing.note}</p>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-16 md:py-24">
          <div className="max-w-[720px] mx-auto px-4 md:px-6">
            <SectionTitle title={copy.faq.title} subtitle="" />
            <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
              {copy.faq.items.map((item, i) => (
                <FAQItem key={i} q={item.q} a={item.a} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24">
          <div className="max-w-[1080px] mx-auto px-4 md:px-6">
            <div className="rounded-3xl bg-[#533afd] text-white p-10 md:p-16 text-center">
              <h2 className="text-3xl md:text-4xl font-semibold tracking-[-0.02em] mb-4">{copy.cta.title}</h2>
              <p className="text-white/80 mb-8 max-w-xl mx-auto">{copy.cta.subtitle}</p>
              <Button size="lg" variant="secondary" onClick={onLogin}>
                {copy.cta.button}
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-card py-12">
        <div className="max-w-[1080px] mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/LOGO.png" alt="TingYue" className="h-7 w-auto" />
                <span className="font-semibold">TingYue</span>
              </div>
              <p className="text-sm text-muted-foreground">{copy.footer.tagline}</p>
            </div>
            <div>
              <div className="text-sm font-medium mb-4">{copy.footer.links.product}</div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {copy.footer.product.map((item, i) => (
                  <li key={i}>
                    <button
                      onClick={() => {
                        if (i === 0) scrollTo('features');
                        else if (i === 1) scrollTo('pricing');
                        else if (i === 2) scrollTo('faq');
                        else onPublicPage('changelog');
                      }}
                      className="hover:text-foreground"
                    >
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-sm font-medium mb-4">{copy.footer.links.company}</div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {copy.footer.company.map((item, i) => (
                  <li key={i}><span className="hover:text-foreground cursor-default">{item}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-sm font-medium mb-4">{copy.footer.links.legal}</div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {copy.footer.legal.map((item, i) => (
                  <li key={i}>
                    <button
                      onClick={() => onPublicPage(i === 0 ? 'terms' : 'privacy')}
                      className="hover:text-foreground"
                    >
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="border-t border-border pt-6 text-xs text-muted-foreground space-y-2">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3">
              <div>© {new Date().getFullYear()} {copy.footer.companyName}. {copy.footer.rights}.</div>
              <div className="flex items-center gap-4">
                <a
                  href="https://beian.miit.gov.cn/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-foreground transition"
                >
                  {copy.footer.icp}
                </a>
              </div>
            </div>
            <p className="text-center md:text-left leading-relaxed">{copy.footer.ownership}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
